// Grouped client for O365 operations
import { ApiConfiguration } from '../ApiConfiguration';
import { Form } from '../models/Form';
import { CalendarEvent } from '../models/CalendarEvent';
import { FilterCalendarEventRequest } from '../models/FilterCalendarEventRequest';
import { DataviewCalendarEventResponse } from '../models/DataviewCalendarEventResponse';
import { FormSerializer } from '../serializers/FormSerializer';
import { CalendarEventSerializer } from '../serializers/CalendarEventSerializer';
import { FilterCalendarEventRequestSerializer } from '../serializers/FilterCalendarEventRequestSerializer';
import { DataviewCalendarEventResponseSerializer } from '../serializers/DataviewCalendarEventResponseSerializer';

/**
 * HTTP client for O365 operations
 *
 * Client for O365 operations
 */
export class O365Client {
  private readonly config: ApiConfiguration;

  constructor(config: ApiConfiguration) {
    this.config = config;
  }

  async getFormForCalendarEvent(formName: string, objectId: string, appointmentId?: string | null, externalId?: string | null, showAs?: string | null, type_?: string | null, sensitivity?: string | null, isCancelled?: boolean | null, isAllDay?: boolean | null, iCalUId?: string | null, start?: string | null, end?: string | null, webLink?: string | null, name?: string | null, description?: string | null, entityId?: string | null, id?: string | null, createdOn?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/o365.Event({objectId})/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      .replace('{objectId}', encodeURIComponent(String(objectId)))
      ;

    const queryParams = new URLSearchParams();
    if (appointmentId !== undefined) {
      queryParams.append('AppointmentId', String(appointmentId));
    }
    if (externalId !== undefined) {
      queryParams.append('ExternalId', String(externalId));
    }
    if (showAs !== undefined) {
      queryParams.append('ShowAs', String(showAs));
    }
    if (type_ !== undefined) {
      queryParams.append('Type', String(type_));
    }
    if (sensitivity !== undefined) {
      queryParams.append('Sensitivity', String(sensitivity));
    }
    if (isCancelled !== undefined) {
      queryParams.append('IsCancelled', String(isCancelled));
    }
    if (isAllDay !== undefined) {
      queryParams.append('IsAllDay', String(isAllDay));
    }
    if (iCalUId !== undefined) {
      queryParams.append('ICalUId', String(iCalUId));
    }
    if (start !== undefined) {
      queryParams.append('Start', String(start));
    }
    if (end !== undefined) {
      queryParams.append('End', String(end));
    }
    if (webLink !== undefined) {
      queryParams.append('WebLink', String(webLink));
    }
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (entityId !== undefined) {
      queryParams.append('EntityId', String(entityId));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (createdOn !== undefined) {
      queryParams.append('CreatedOn', String(createdOn));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async getFormForTypeCalendarEvent(formName: string, appointmentId?: string | null, externalId?: string | null, showAs?: string | null, type_?: string | null, sensitivity?: string | null, isCancelled?: boolean | null, isAllDay?: boolean | null, iCalUId?: string | null, start?: string | null, end?: string | null, webLink?: string | null, name?: string | null, description?: string | null, entityId?: string | null, id?: string | null, createdOn?: string | null, options?: { headers?: Record<string, string> }): Promise<Form> {
    let path = '/app/api/Object/o365.Event/{formName}/DataForm'
      .replace('{formName}', encodeURIComponent(String(formName)))
      ;

    const queryParams = new URLSearchParams();
    if (appointmentId !== undefined) {
      queryParams.append('AppointmentId', String(appointmentId));
    }
    if (externalId !== undefined) {
      queryParams.append('ExternalId', String(externalId));
    }
    if (showAs !== undefined) {
      queryParams.append('ShowAs', String(showAs));
    }
    if (type_ !== undefined) {
      queryParams.append('Type', String(type_));
    }
    if (sensitivity !== undefined) {
      queryParams.append('Sensitivity', String(sensitivity));
    }
    if (isCancelled !== undefined) {
      queryParams.append('IsCancelled', String(isCancelled));
    }
    if (isAllDay !== undefined) {
      queryParams.append('IsAllDay', String(isAllDay));
    }
    if (iCalUId !== undefined) {
      queryParams.append('ICalUId', String(iCalUId));
    }
    if (start !== undefined) {
      queryParams.append('Start', String(start));
    }
    if (end !== undefined) {
      queryParams.append('End', String(end));
    }
    if (webLink !== undefined) {
      queryParams.append('WebLink', String(webLink));
    }
    if (name !== undefined) {
      queryParams.append('Name', String(name));
    }
    if (description !== undefined) {
      queryParams.append('Description', String(description));
    }
    if (entityId !== undefined) {
      queryParams.append('EntityId', String(entityId));
    }
    if (id !== undefined) {
      queryParams.append('_id', String(id));
    }
    if (createdOn !== undefined) {
      queryParams.append('CreatedOn', String(createdOn));
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${this.config.baseUrl}${path}?${queryString}` : `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'GET',
      headers: {
        ...this.config.headers,
        ...options?.headers,
      },
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return FormSerializer.fromObject(data);
  }

  async filterCalendarEvent(request?: FilterCalendarEventRequest | null, options?: { headers?: Record<string, string> }): Promise<CalendarEvent[]> {
    const path = '/app/api/Object/o365.Event/Filter';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterCalendarEventRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => CalendarEventSerializer.fromObject(item));
  }

  async recentCalendarEvent(request?: FilterCalendarEventRequest | null, options?: { headers?: Record<string, string> }): Promise<CalendarEvent[]> {
    const path = '/app/api/Object/o365.Event/Recent';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterCalendarEventRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return (data as any[]).map(item => CalendarEventSerializer.fromObject(item));
  }

  async dataViewCalendarEvent(request?: FilterCalendarEventRequest | null, options?: { headers?: Record<string, string> }): Promise<DataviewCalendarEventResponse> {
    const path = '/app/api/Object/o365.Event/DataView';

    const url = `${this.config.baseUrl}${path}`;

    const requestInit: RequestInit = {
      method: 'POST',
      headers: {
        ...this.config.headers,
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: request ? JSON.stringify(FilterCalendarEventRequestSerializer.toObject(request)) : undefined,
    };

    const response = await this.config.executeRequest(url, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return DataviewCalendarEventResponseSerializer.fromObject(data);
  }

}
